import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { ChevronRight, Heart, ShoppingCart } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { useFavorites } from "../../context/FavoritesContext";
import ShareButton from "../../Components/ShareButton";
import "./combo.css";
import "../pop/ProductDetail.css";
import { fetchCombos, fetchComboById } from "../../api/combos.api";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

const toAbsoluteUrl = (value) => {
    const path = String(value || "").trim();
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    if (!API_BASE_URL) return path;
    return path.startsWith("/") ? `${API_BASE_URL}${path}` : `${API_BASE_URL}/${path}`;
};

const ComboDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { isMember, isAuthenticated, setShowLoginModal } = useAuth();
    const { updateCart, getComboQuantity, isComboInCart } = useCart();
    const { toggleFavorite, isFavorite } = useFavorites();

    const [combo, setCombo] = useState(null);
    const [allCombos, setAllCombos] = useState([]);
    const [selectedImage, setSelectedImage] = useState(0);
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        let mounted = true;

        fetchComboById(id)
            .then((data) => {
                if (!mounted) return;
                setCombo(data);
            })
            .catch(() => {
                if (!mounted) return;
                setCombo(null);
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [id]);


    useEffect(() => {
        let mounted = true;

        fetchCombos()
            .then((data) => {
                if (!mounted) return;
                setAllCombos(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (mounted) setAllCombos([]);
            });

        return () => {
            mounted = false;
        };
    }, []);


    const images = useMemo(() => {
        const mainImage = toAbsoluteUrl(combo?.image);

        const rowImages = Array.isArray(combo?.rows)
            ? [...combo.rows]
                .filter((row) => row?.image && row.checked !== false)
                .sort(
                    (a, b) =>
                        Number(a?.order_number || 0) - Number(b?.order_number || 0)
                )
                .map((row) => toAbsoluteUrl(row.image))
                .filter(Boolean)
            : [];

        const gallery = Array.from(
            new Set([mainImage, ...rowImages].filter(Boolean))
        ).slice(0, 4);

        return gallery.length ? gallery : [mainImage].filter(Boolean);
    }, [combo]);

    useEffect(() => {
        setSelectedImage(0);
    }, [combo?.id]);

    if (loading || !combo) {
        return (
            <div className="loading-wrap">
                <div className="loader" />
                <p>Loading combo</p>
            </div>
        );
    }

    const comboQty = getComboQuantity(combo.id);
    const isInCart = isComboInCart(combo.id);
    const isSoldOut = combo.max_unit === 0;
    const baseComboPoints = Number(combo.points);
    const memberComboPoints = Number(combo.member_points ?? combo.memberPoints);
    const comboPoints =
        isMember && Number.isFinite(memberComboPoints) && memberComboPoints > 0
            ? memberComboPoints
            : baseComboPoints;
    const showComboPointsBadge =
        Number.isFinite(comboPoints) && comboPoints > 0;

    const handleAddCombo = (targetCombo = combo) => {
        if (!targetCombo) return;
        if (!isAuthenticated) {
            setShowLoginModal(true);
            return;
        }

        if ((targetCombo.max_unit ?? 0) === 0) return;

        updateCart({
            type: "combo",
            id: targetCombo.id,
            delta: 1,
        });
    };

    const relatedCombos = allCombos
        .filter(
            (c) => c.brand === combo.brand && c.id !== combo.id
        )
        .slice(0, 4);

    return (
        <div className="page">
            <Helmet>
                <title>{`${combo.name} Combo | 164 Diecast Zone`}</title>
                <meta
                    name="description"
                    content={`Buy the ${combo.name} combo set online at 164 Diecast Zone — premium 1:64 diecast model cars bundled at a special price.`}
                />
                <link rel="canonical" href={`https://164diecastzone.com/combo/${combo.id}`} />
                <meta property="og:title" content={`${combo.name} Combo | 164 Diecast Zone`} />
                <meta
                    property="og:description"
                    content={`Buy the ${combo.name} combo set online at 164 Diecast Zone — premium 1:64 diecast model cars bundled at a special price.`}
                />
                {combo.image && <meta property="og:image" content={toAbsoluteUrl(combo.image)} />}
                <meta property="og:url" content={`https://164diecastzone.com/combo/${combo.id}`} />
            </Helmet>

            {/* 🔹 Breadcrumb */}
            <div className="breadcrumb11">
                <div className="page-container">
                    <span onClick={() => navigate("/")}>Home</span>
                    <span className="highlight"> / </span>{" "}
                    <span class='breadcrumb-link' onClick={() => navigate(`/combos`)}>Combo Sale</span>
                    <span className="highlight"> / </span>{" "}
                    <span> {combo.name}</span>
                </div>

            </div>


            <div className="detail-content">
                <div className="detail-grid">

                    {/* 🔹 Images */}
                    <div className="pd-media">

                        <div className="pd-main-image">
                            <img
                                src={images[selectedImage] || toAbsoluteUrl(combo.image)}
                                alt={combo.name}
                            />
                        </div>

                        <div className="pd-thumbnails">
                            {images.map((img, idx) => (
                                <img
                                    key={idx}
                                    src={img}
                                    className={`thumbnail ${selectedImage === idx ? "thumbnail-active" : ""}`}
                                    onClick={() => setSelectedImage(idx)}
                                    alt={`combo-thumb-${idx}`}
                                />
                            ))}
                        </div>

                    </div>

                    {/* 🔹 Info */}
                    <div className="pd-info">

                        <div className="pd-brand">{combo.brand}</div>

                        <h1 className="pd-title">{combo.name}</h1>

                        <div className="pd-price-row">
                            <span className="pd-price">₹{combo.price}</span>
                        </div>

                        {showComboPointsBadge && (
                            <div className="pd-points">
                                Earn ₹{comboPoints} Rewards on this purchase
                            </div>
                        )}

                        <div className="pd-actions">

                            {/* Quantity */}
                            {isInCart && !isSoldOut && (
                                <div className="pd-qty">
                                    <button onClick={() =>
                                        updateCart({ type: "combo", id: combo.id, delta: -1, })} >-</button>

                                    <span>{comboQty}</span>

                                    <button
                                        onClick={() =>
                                            updateCart({ type: "combo", id: combo.id, delta: 1, })}
                                    >+</button>
                                </div>
                            )}

                            {/* Add to cart */}
                            {!isSoldOut && !isInCart && (
                                <button className="pd-add-cart" onClick={() => handleAddCombo()}>
                                    Add to Cart
                                </button>
                            )}

                            {isSoldOut && (
                                <button className="pd-soldout" disabled>
                                    SOLD OUT
                                </button>
                            )}

                            {/* Favorite */}
                            <button
                                className={`pd-fav ${isFavorite(combo.id) ? "active" : ""}`}
                                onClick={() => toggleFavorite(combo.id)}
                            >
                                <Heart
                                    size={22}
                                    fill={isFavorite(combo.id) ? "currentColor" : "none"}
                                />
                            </button>

                            <ShareButton
                                title={combo.name}
                                text={`Check out ${combo.name} on DiecastZone`}
                            />

                        </div>

                        {/* Details */}
                        <div className="pd-details">
                            <h3>Product Details</h3>
                            <ul className="product-list">
                                {combo.products.map((d) => (
                                    <li key={d.id} className="product-item">
                                        <span>{d.name}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                    </div>

                </div>

                {/* 🔹 Related Combos */}
                <div className="related-section">
                    <div className="container">

                        <div className="related-header">
                            <h2 className="related-title">
                                RELATED <span className="related-accent">PRODUCTS</span>
                            </h2>

                            {relatedCombos.length > 0 && (
                                <button
                                    onClick={() => navigate("/combos")}
                                    className="view-more-btn"
                                >
                                    View more Products <ChevronRight size={20} />
                                </button>
                            )}
                        </div>

                        {relatedCombos.length > 0 ? (
                            <div className="related-grid">
                                {relatedCombos.map((product) => {
                                    const mrp = product.combo_price;
                                    const price = product.price;
                                    const basePoints = Number(product.points);
                                    const memberPoints = Number(
                                        product.member_points ?? product.memberPoints
                                    );
                                    const displayPoints =
                                        isMember ? memberPoints : basePoints;
                                    const currentQty = getComboQuantity(product.id);
                                    const isRelatedInCart = isComboInCart(product.id);
                                    const maxUnit = product.max_unit ?? 0;
                                    const isRelatedSoldOut = maxUnit === 0;
                                    const isMaxReached = maxUnit > 0 && currentQty >= maxUnit;
                                    const showRelatedPointsBadge =
                                        Number.isFinite(displayPoints) &&
                                        displayPoints > 0;

                                    const discount =
                                        mrp && mrp > price
                                            ? Math.round(((mrp - price) / mrp) * 100)
                                            : 0;

                                    return (
                                        <div key={product.id} className="related-card">
                                            <div className="related-image-wrapper">
                                                {product.is_new_arrival ? (
                                                    <div className="related-badge">New Arrival</div>
                                                ) : product.is_trending ? (
                                                    <div className="related-badge">Trending Now</div>
                                                ) : null}

                                                <img
                                                    src={product.image}
                                                    alt={product.name}
                                                    className="related-image"
                                                    onClick={() => navigate(`/combo/${product.id}`)}
                                                />

                                                {showRelatedPointsBadge && (
                                                    <div className="related-points-badge">₹{displayPoints} Rewards</div>
                                                )}
                                            </div>

                                            <div className="related-info">
                                                <h3
                                                    className="related-name"
                                                    onClick={() => navigate(`/combo/${product.id}`)}
                                                >
                                                    {product.name}
                                                </h3>

                                                <div className="related-brand">
                                                    {product.brand}
                                                </div>

                                                {isMember && mrp && mrp > price ? (
                                                    <>
                                                        <div className="related-mrp-row">
                                                            <span className="related-mrp-label">MRP:</span>
                                                            <span className="related-mrp">₹{mrp}</span>

                                                            {discount >= 10 && (
                                                                <span className="related-discount">
                                                                    {discount}% OFF
                                                                </span>
                                                            )}
                                                        </div>

                                                        <div className="related-price-row">
                                                            <div className="related-price">
                                                                ₹{product.price}
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="related-price-row">
                                                        <div className="related-price">
                                                            ₹{product.price}
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="related-action-row">
                                                    {isRelatedSoldOut ? (
                                                        <button className="related-cart-btn related-cart-btn-disabled" disabled>
                                                            Sold Out
                                                        </button>
                                                    ) : !isRelatedInCart ? (
                                                        <button
                                                            className="related-cart-btn"
                                                            onClick={() => handleAddCombo(product)}
                                                        >
                                                            <ShoppingCart size={16} />
                                                            Add To Cart
                                                        </button>
                                                    ) : (
                                                        <div className="related-qty">
                                                            <button
                                                                onClick={() =>
                                                                    updateCart({
                                                                        type: "combo",
                                                                        id: product.id,
                                                                        delta: -1,
                                                                    })
                                                                }
                                                            >
                                                                -
                                                            </button>
                                                            <span>{currentQty}</span>
                                                            <button
                                                                onClick={() =>
                                                                    updateCart({
                                                                        type: "combo",
                                                                        id: product.id,
                                                                        delta: 1,
                                                                    })
                                                                }
                                                                disabled={isMaxReached}
                                                            >
                                                                +
                                                            </button>
                                                        </div>
                                                    )}

                                                    <button
                                                        className={`related-fav-btn ${isFavorite(product.id) ? "active" : ""}`}
                                                        onClick={() => toggleFavorite(product.id)}
                                                    >
                                                        <Heart size={18} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="related-empty">
                                <p>No related products available.</p>

                                {/* <button
                                    className="view-more-btn"
                                    onClick={() => navigate("/combos")}
                                >
                                    Browse All Products
                                </button> */}
                            </div>
                        )}

                    </div>
                </div>



            </div>
        </div>
    );
};

export default ComboDetail;
